"use client";

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { useWallet } from "@aptos-labs/wallet-adapter-react";

interface ConnectionContextType {
  isConnected: boolean;
  isAuthenticated: boolean;
  walletAddress?: string;
  connectWallet: (address?: string) => void;
  disconnectWallet: () => void;
  setIsAuthenticated: (value: boolean) => void;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const { connected, account } = useWallet();
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | undefined>();

  // Sync with actual wallet connection state
  useEffect(() => {
    if (connected && account) {
      setIsConnected(true);
      setWalletAddress(account.address.toString());
    } else {
      setIsConnected(false);
      setWalletAddress(undefined);
      // Don't reset auth state here - cookie might still be valid
      // Auth state is managed by AuthInitializer based on cookie validation
    }
  }, [connected, account]);

  const connectWallet = useCallback((address?: string) => {
    // This is called after successful SIWA authentication or when session is restored
    setIsConnected(true);
    if (address) {
      setWalletAddress(address);
    } else if (account) {
      setWalletAddress(account.address.toString());
    }
  }, [account]);

  const disconnectWallet = useCallback(() => {
    setIsConnected(false);
    setIsAuthenticated(false);
    setWalletAddress(undefined);
    // Clear any stored auth data
    if (walletAddress) {
      localStorage.removeItem(`siwa_auth_${walletAddress}`);
    }
  }, [walletAddress]);

  return (
    <ConnectionContext.Provider
      value={{
        isConnected,
        isAuthenticated,
        walletAddress,
        connectWallet,
        disconnectWallet,
        setIsAuthenticated,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection() {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error("useConnection must be used within a ConnectionProvider");
  }
  return context;
}
